import type { CaseManifestV1 } from "@cold-case/shared/case-manifest";
import type Phaser from "phaser";

import { isAccusationPhaseActive } from "../game/accusation-controller.js";
import { resolveBuildingTitleFromCopy } from "./building-display.js";
import { emitLeaderMirror, leaderHasFollowers } from "../game/leader-follow-mirror.js";
import type { LeaderMirrorNarrative } from "@cold-case/shared/player-follow";
import {
  hideNarrativeNudgeUi,
  syncNarrativeNudgeUi,
  wireNarrativeNudgeButton,
} from "./follow-nudge-ui.js";
import { ensureGlobalCaptureEnabled } from "./phaser-keyboard-typing.js";
import {
  focusPlayfieldCanvas,
  releaseDomOverlayFocus,
  setPlayerOverlayOpen,
} from "./player-input-gate.js";

export type OpenNarrativeModalOptions = {
  title: string;
  bodyText: string;
  /** Scene description shown above investigation outcome text. */
  leadText?: string;
  buildingId?: string;
  /** When set, title is resolved via friendly building name rules. */
  manifest?: CaseManifestV1;
  decor?: boolean;
  /** Follower mirror — show content but no local dismiss. */
  readOnly?: boolean;
  phaserGame: Phaser.Game | null;
  /** Runs once after the overlay is dismissed (Close, Escape, backdrop). */
  onClose?: () => void;
};

let mirrorReadOnly = false;

function emitNarrativeMirror(payload: LeaderMirrorNarrative): void {
  if (mirrorReadOnly) return;
  if (!leaderHasFollowers()) return;
  emitLeaderMirror(payload);
}

let pendingOnClose: (() => void) | undefined;
let uiAbort: AbortController | null = null;
let modalPhaserGame: Phaser.Game | null = null;

export function isNarrativeModalOpen(): boolean {
  if (typeof document === "undefined") return false;
  return document.getElementById("narrative-overlay")?.classList.contains("open") === true;
}

export function closeNarrativeModal(): void {
  uiAbort?.abort();
  uiAbort = null;

  const overlay = document.getElementById("narrative-overlay");
  if (overlay) {
    releaseDomOverlayFocus(overlay);
    overlay.classList.remove("open");
    overlay.classList.remove("narrative-follower-readonly");
    overlay.setAttribute("aria-hidden", "true");
  }

  const fn = pendingOnClose;
  pendingOnClose = undefined;

  setPlayerOverlayOpen("narrative", false);
  const game = modalPhaserGame;
  modalPhaserGame = null;
  if (game) {
    game.events.emit("ccc_narrative_modal_closed");
  }

  emitNarrativeMirror({ kind: "narrative", open: false });

  ensureGlobalCaptureEnabled();
  focusPlayfieldCanvas();

  mirrorReadOnly = false;
  hideNarrativeNudgeUi();
  if (fn) fn();
}

export function openNarrativeModal(opts: OpenNarrativeModalOptions): void {
  closeNarrativeModal();

  if (isAccusationPhaseActive()) {
    return;
  }

  const overlay = document.getElementById("narrative-overlay");
  const titleEl = document.getElementById("narrative-title");
  const bodyEl = document.getElementById("narrative-body");
  const closeBtn = document.getElementById("narrative-close") as HTMLButtonElement | null;
  if (!overlay || !titleEl || !bodyEl || !closeBtn) return;

  opts.phaserGame?.events.emit("ccc_close_journal");

  pendingOnClose = opts.onClose;
  mirrorReadOnly = opts.readOnly === true;

  setPlayerOverlayOpen("narrative", mirrorReadOnly ? false : true);
  modalPhaserGame = opts.phaserGame;
  if (modalPhaserGame) {
    modalPhaserGame.events.emit("ccc_narrative_modal_shown");
  }

  const manifest =
    opts.manifest ??
    (opts.phaserGame?.registry.get("ccc") as { caseManifest?: CaseManifestV1 } | undefined)
      ?.caseManifest;
  const displayTitle = resolveBuildingTitleFromCopy(
    manifest,
    opts.buildingId,
    opts.title,
    { decor: opts.decor }
  );
  titleEl.textContent = displayTitle;
  const lead = opts.leadText?.trim();
  bodyEl.textContent = lead
    ? `${lead}\n\n${opts.bodyText || ""}`.trim()
    : opts.bodyText || "";

  overlay.classList.add("open");
  overlay.setAttribute("aria-hidden", "false");
  overlay.classList.toggle("narrative-follower-readonly", mirrorReadOnly);
  syncNarrativeNudgeUi({ readOnly: mirrorReadOnly });

  emitNarrativeMirror({
    kind: "narrative",
    open: true,
    title: displayTitle,
    leadText: opts.leadText,
    bodyText: opts.bodyText,
    buildingId: opts.buildingId,
  });

  uiAbort = new AbortController();
  const { signal } = uiAbort;

  const dismiss = () => {
    if (mirrorReadOnly) return;
    closeNarrativeModal();
  };

  closeBtn.style.pointerEvents = mirrorReadOnly ? "none" : "";
  closeBtn.disabled = mirrorReadOnly;

  if (!mirrorReadOnly) {
    closeBtn.addEventListener("click", dismiss, { signal });

    overlay.addEventListener(
      "click",
      (ev) => {
        if (ev.target === overlay) dismiss();
      },
      { signal }
    );

    document.addEventListener(
      "keydown",
      (ev) => {
        if (ev.key === "Escape") {
          ev.preventDefault();
          dismiss();
        }
      },
      { signal }
    );

    queueMicrotask(() => closeBtn.focus());
  } else {
    wireNarrativeNudgeButton(signal);
  }
}
